import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  SPACE_FIELD_META,
  SPACE_GENDERS,
  SPACE_TYPES,
  spaceCreateSchema,
} from "@leopart/shared/contracts/space.contract";
import type { SpaceCreateInput } from "@leopart/shared/contracts/space.contract";
import {
  contractResolver,
  zodToFieldDefs,
} from "../../shared/CustomFormV3/contractForm";
import { applyServerErrors } from "../../shared/CustomFormV3/serverErrors";
import { ISpace } from "../../models/Space";
import { useEndpoints } from "../../hooks/useEndpoints";
import { ConfirmModalContext } from "../../providers/confirmModalProvider";

/**
 * Logica di AddSpaceModal — form CONTRACT-DRIVEN:
 * schema zod + FIELD_META di `@leopart/shared/contracts/space.contract` sono
 * l'unica fonte per campi, label, options e validazione client (stesso schema
 * che governa il DTO backend). I 400 di validazione del server tornano sotto
 * ai campi via `applyServerErrors`.
 */

const DEFAULT_VALUES: SpaceCreateInput = {
  name: "",
  type: SPACE_TYPES.SOCCER,
  gender: SPACE_GENDERS.MALE,
};

interface UseAddSpaceModalArgs {
  isOpen?: boolean | ISpace;
  onClose: () => void;
  onDelete: () => void;
  onConfirm: (form: SpaceCreateInput) => void | Promise<void>;
}

export const useAddSpaceModal = ({
  isOpen,
  onClose,
  onDelete,
  onConfirm,
}: UseAddSpaceModalArgs) => {
  const { deleteSpace } = useEndpoints();
  const { setState } = useContext(ConfirmModalContext);
  const [currentTab, setCurrentTab] = useState(0);
  const isAddMode = typeof isOpen === "boolean";

  const methods = useForm<SpaceCreateInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: contractResolver<SpaceCreateInput>(spaceCreateSchema),
  });

  /** FieldDef generati dal contract (name / type / gender). */
  const baseFieldDefs = useMemo(
    () => zodToFieldDefs(spaceCreateSchema, SPACE_FIELD_META),
    []
  );

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCurrentTab(0);
        methods.reset(DEFAULT_VALUES);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (typeof isOpen !== "boolean") {
      // Gli enum FE (SPACE_TYPES/SPACE_GENDERS) hanno gli stessi valori del
      // contract (derivato dagli enum backend): il cast è sicuro.
      methods.reset({
        name: isOpen.name,
        type: isOpen.type as unknown as SpaceCreateInput["type"],
        gender: isOpen.gender as unknown as SpaceCreateInput["gender"],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stessa condizione di abilitazione del salvataggio del vecchio modal
  const [nameValue, typeValue] = methods.watch(["name", "type"]);
  const isSaveDisabled = !nameValue?.trim() || !typeValue;

  const knownFields = useMemo(
    () => baseFieldDefs.map((field) => field.key),
    [baseFieldDefs]
  );

  const submit = methods.handleSubmit(async (values) => {
    try {
      await onConfirm(values);
    } catch (error) {
      // 400 di validazione backend → errore sotto al campo (stesso schema
      // del contract lato server); il toast generico lo mostra già il parent.
      applyServerErrors(error, methods.setError, { knownFields });
    }
  });

  const handleDeleteSpace = () => {
    const spaceName = (isOpen as ISpace)?.name || "";
    const spaceId = (isOpen as ISpace)?._id || "";
    // Chiudi prima AddSpaceModal per evitare conflitti tra due Chakra modal aperte insieme
    onClose();
    setState({
      title: "Attenzione",
      description:
        "L'operazione è permanente: verranno rimossi anche i team dello spazio e i loro eventi futuri non compilati.",
      onConfirm: async () => {
        const toastId = toast.loading(
          `Rimozione spazio ${spaceName} in corso...`,
          {
            duration: 20000,
          }
        );
        try {
          await deleteSpace(spaceId);
          toast.success(`${spaceName} rimosso`, { id: toastId });
          onDelete();
        } catch (error) {
          toast.error("Errore durante la rimozione dello spazio, riprova", {
            id: toastId,
          });
        }
      },
    });
  };

  return {
    methods,
    baseFieldDefs,
    isAddMode,
    currentTab,
    setCurrentTab,
    isSaveDisabled,
    submit,
    handleDeleteSpace,
  };
};
